import React, { useState, useEffect, useRef } from 'react';
import { Maximize2 } from 'lucide-react';
import './PoseMediaTray.css';

export function PoseMediaTray({
  mediaList = [],
  onDeleteItems,
  onClearAll,
}) {
  const [filterType, setFilterType] = useState('all'); // 'all' | 'video' | 'image'
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [previewItem, setPreviewItem] = useState(null);
  const modalRef = useRef(null);

  const videoCount = mediaList.filter((item) => item.type === 'video').length;
  const imageCount = mediaList.filter((item) => item.type === 'image').length;

  const filteredList = mediaList.filter((item) => {
    if (filterType === 'video') return item.type === 'video';
    if (filterType === 'image') return item.type === 'image';
    return true;
  });

  const isAllSelected =
    filteredList.length > 0 &&
    filteredList.every((item) => selectedIds.has(item.id));

  // 크게 보기 모달 열릴 때 중앙 포커싱 및 ESC 닫기 리스너 등록
  useEffect(() => {
    if (previewItem) {
      if (modalRef.current) {
        modalRef.current.focus();
        modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          setPreviewItem(null);
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [previewItem]);

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      const next = new Set(selectedIds);
      filteredList.forEach((item) => next.add(item.id));
      setSelectedIds(next);
    }
  };

  const handleToggleSelectItem = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const downloadItem = (item) => {
    if (!item?.url) return;
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = item.url;
    a.download = item.name || `aimus-media-${Date.now()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadSelected = () => {
    const itemsToDownload = mediaList.filter((item) => selectedIds.has(item.id));
    if (itemsToDownload.length === 0) return;

    itemsToDownload.forEach((item, index) => {
      // 브라우저 동시 다운로드 차단 방지를 위해 시차 다운로드
      setTimeout(() => {
        downloadItem(item);
      }, index * 180);
    });
  };

  const handleDownloadAll = () => {
    if (mediaList.length === 0) return;
    mediaList.forEach((item, index) => {
      setTimeout(() => {
        downloadItem(item);
      }, index * 180);
    });
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return;
    const idsToDelete = Array.from(selectedIds);
    onDeleteItems(idsToDelete);
    setSelectedIds(new Set());
    if (previewItem && selectedIds.has(previewItem.id)) {
      setPreviewItem(null);
    }
  };

  const handleDeleteSingle = (id) => {
    onDeleteItems([id]);
    const next = new Set(selectedIds);
    next.delete(id);
    setSelectedIds(next);
    if (previewItem?.id === id) {
      setPreviewItem(null);
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  };

  if (mediaList.length === 0) {
    return (
      <div className="pose-media-tray pose-media-tray--empty">
        <div className="pose-media-tray__header">
          <div className="pose-media-tray__title-group">
            <h4 className="pose-media-tray__title">분석 미디어 보관함</h4>
            <span className="pose-media-tray__count-badge">0개 저장됨</span>
          </div>
        </div>
        <p className="pose-media-tray__empty-text">
          촬영 화면 캡처 또는 AI 사격 녹화 시 파일이 바로 다운로드되지 않고 이 보관함에 안전하게 임시 저장됩니다.
          카드를 클릭하여 선택 후 일괄 다운로드하거나, 크게 보기 아이콘으로 확인하세요.
        </p>
      </div>
    );
  }

  return (
    <div className="pose-media-tray">
      {/* 1. 보관함 헤더 및 탭/선택 툴바 */}
      <div className="pose-media-tray__header">
        <div className="pose-media-tray__title-group">
          <h4 className="pose-media-tray__title">분석 미디어 보관함</h4>
          <span className="pose-media-tray__count-badge">총 {mediaList.length}개</span>
        </div>

        {/* 미디어 유형 필터 탭 */}
        <div className="pose-media-tray__tabs">
          <button
            type="button"
            className={`pose-media-tray__tab ${filterType === 'all' ? 'pose-media-tray__tab--active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            전체 ({mediaList.length})
          </button>
          <button
            type="button"
            className={`pose-media-tray__tab ${filterType === 'video' ? 'pose-media-tray__tab--active' : ''}`}
            onClick={() => setFilterType('video')}
          >
            영상 ({videoCount})
          </button>
          <button
            type="button"
            className={`pose-media-tray__tab ${filterType === 'image' ? 'pose-media-tray__tab--active' : ''}`}
            onClick={() => setFilterType('image')}
          >
            이미지 ({imageCount})
          </button>
        </div>
      </div>

      {/* 2. 일괄 제어 툴바 */}
      <div className="pose-media-tray__toolbar">
        <div className="pose-media-tray__selection-info">
          <button
            type="button"
            className="pose-media-tray__select-toggle-btn"
            onClick={handleToggleSelectAll}
          >
            {isAllSelected ? '전체 해제' : '전체 선택'}
          </button>
          <span className="pose-media-tray__selected-count">
            선택: {selectedIds.size}개
          </span>
        </div>

        <div className="pose-media-tray__actions">
          <button
            type="button"
            className="pose-media-tray__btn pose-media-tray__btn--primary"
            onClick={handleDownloadSelected}
            disabled={selectedIds.size === 0}
          >
            선택 다운로드 ({selectedIds.size})
          </button>
          <button
            type="button"
            className="pose-media-tray__btn pose-media-tray__btn--secondary"
            onClick={handleDownloadAll}
          >
            전체 다운로드
          </button>
          <button
            type="button"
            className="pose-media-tray__btn pose-media-tray__btn--danger"
            onClick={handleDeleteSelected}
            disabled={selectedIds.size === 0}
          >
            선택 삭제
          </button>
          <button
            type="button"
            className="pose-media-tray__btn pose-media-tray__btn--ghost"
            onClick={onClearAll}
          >
            보관함 비우기
          </button>
        </div>
      </div>

      {/* 3. 미디어 카드 그리드 */}
      <div className="pose-media-tray__grid">
        {filteredList.map((item) => {
          const isSelected = selectedIds.has(item.id);

          return (
            <div
              key={item.id}
              className={`pose-media-card ${isSelected ? 'pose-media-card--selected' : ''}`}
              onClick={() => handleToggleSelectItem(item.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggleSelectItem(item.id);
                }
              }}
            >
              {/* 상단 미디어 타입 태그 및 크게 보기 아이콘 버튼 */}
              <div className="pose-media-card__top">
                <span
                  className={`pose-media-card__type-tag ${
                    item.type === 'video'
                      ? 'pose-media-card__type-tag--video'
                      : 'pose-media-card__type-tag--image'
                  }`}
                >
                  {item.type === 'video' ? '영상' : '이미지'}
                </span>

                {/* 크게 보기 아이콘 버튼 */}
                <button
                  type="button"
                  className="pose-media-card__zoom-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewItem(item);
                  }}
                  title="크게 보기"
                  aria-label="크게 보기"
                >
                  <Maximize2 size={14} />
                </button>
              </div>

              {/* 썸네일 / 프리뷰 영역 */}
              <div className="pose-media-card__preview">
                {item.type === 'video' ? (
                  <video
                    src={item.url}
                    className="pose-media-card__video-thumb"
                    muted
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={item.url}
                    alt={item.name}
                    className="pose-media-card__img-thumb"
                    loading="lazy"
                  />
                )}
                <div className="pose-media-card__overlay">
                  <span>{isSelected ? '선택 해제' : '클릭하여 선택'}</span>
                </div>
              </div>

              {/* 정보 영역 */}
              <div className="pose-media-card__info">
                <span className="pose-media-card__name" title={item.name}>
                  {item.name}
                </span>
                <div className="pose-media-card__meta">
                  <span className="pose-media-card__time">{formatTime(item.createdAt)}</span>
                  {item.type === 'video' && item.duration > 0 && (
                    <span className="pose-media-card__duration">
                      {Math.floor(item.duration / 60)}분 {item.duration % 60}초
                    </span>
                  )}
                </div>
              </div>

              {/* 개별 액션 버튼 */}
              <div className="pose-media-card__actions">
                <button
                  type="button"
                  className="pose-media-card__btn pose-media-card__btn--download"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadItem(item);
                  }}
                >
                  저장
                </button>
                <button
                  type="button"
                  className="pose-media-card__btn pose-media-card__btn--delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSingle(item.id);
                  }}
                >
                  삭제
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* 4. 미디어 상세 모달 (크게 확인하기) */}
      {previewItem && (
        <div className="pose-media-modal-backdrop" onClick={() => setPreviewItem(null)}>
          <div
            ref={modalRef}
            className="pose-media-modal"
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            <div className="pose-media-modal__header">
              <div className="pose-media-modal__title-group">
                <span
                  className={`pose-media-card__type-tag ${
                    previewItem.type === 'video'
                      ? 'pose-media-card__type-tag--video'
                      : 'pose-media-card__type-tag--image'
                  }`}
                >
                  {previewItem.type === 'video' ? '영상 분석 파일' : '자세 캡처 이미지'}
                </span>
                <span className="pose-media-modal__name">{previewItem.name}</span>
              </div>
              <button
                type="button"
                className="pose-media-modal__close-btn"
                onClick={() => setPreviewItem(null)}
              >
                닫기
              </button>
            </div>

            <div className="pose-media-modal__body">
              {previewItem.type === 'video' ? (
                <video
                  src={previewItem.url}
                  className="pose-media-modal__video"
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <img
                  src={previewItem.url}
                  alt={previewItem.name}
                  className="pose-media-modal__image"
                />
              )}
            </div>

            <div className="pose-media-modal__footer">
              <span className="pose-media-modal__meta-text">
                생성 시간: {formatTime(previewItem.createdAt)}
              </span>
              <div className="pose-media-modal__footer-actions">
                <button
                  type="button"
                  className="pose-media-tray__btn pose-media-tray__btn--primary"
                  onClick={() => downloadItem(previewItem)}
                >
                  이 파일 저장 (다운로드)
                </button>
                <button
                  type="button"
                  className="pose-media-tray__btn pose-media-tray__btn--danger"
                  onClick={() => handleDeleteSingle(previewItem.id)}
                >
                  보관함에서 삭제
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
